import React from 'react';
import { PublisherStatusData } from '../useAdminQueues';

interface PublishSchedulerBlockProps {
  publisherStatus: PublisherStatusData | null;
  onToggle: () => void;
}

const formatFireTime = (iso: string | null, tz: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
      timeZoneName: 'short',
    });
  } catch {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
};

/** Superadmin: background publish scheduler — queue drain status + pause. */
const PublishSchedulerBlock: React.FC<PublishSchedulerBlockProps> = ({
  publisherStatus,
  onToggle,
}) => {
  if (!publisherStatus) return null;
  const { scheduler, queue, next } = publisherStatus;
  const activeWork = queue.scheduled + queue.publishing;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="tend-micro">Background Publisher</span>
        <span
          title={
            scheduler.paused
              ? 'Paused — nothing will publish'
              : activeWork > 0
              ? 'Publishing on cadence'
              : 'Idle — nothing scheduled'
          }
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: scheduler.paused
              ? 'var(--signal-rec-soft)'
              : activeWork > 0
              ? 'var(--signal-live)'
              : 'rgba(255,255,255,.3)',
            boxShadow:
              !scheduler.paused && activeWork > 0
                ? '0 0 8px rgba(68,211,106,.7)'
                : 'none',
          }}
        />
        <button
          type="button"
          className="tend-btn tend-btn--glass"
          style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: 12 }}
          onClick={onToggle}
        >
          {scheduler.paused ? 'Resume' : 'Pause'}
        </button>
      </div>

      {scheduler.paused && (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--signal-rec-soft)' }}>
          Publishing paused — queues hold until resumed (survives restarts).
        </p>
      )}

      <div
        style={{
          marginTop: 8,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 12,
          color: 'var(--text-3)',
        }}
      >
        <span>
          <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{queue.scheduled}</span> scheduled ·{' '}
          <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{queue.queued}</span> queued
        </span>
        <span>{queue.published_today} published today</span>
      </div>

      {!scheduler.paused && next && (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
          Next: {next.brand_slug} → {next.platform} at{' '}
          {formatFireTime(next.scheduled_at, scheduler.timezone)}
        </p>
      )}

      {queue.failed > 0 && (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--signal-rec-soft)' }}>
          {queue.failed} failed ticket{queue.failed !== 1 ? 's' : ''} parked — re-queue from the brand's
          Tended page to retry
        </p>
      )}
    </div>
  );
};

export default PublishSchedulerBlock;
